import { RepresentativeItem } from './RepresentativeItem'
import {
  TBusinessAccountDetails,
  TBusinessAccountDocument,
  TBusinessAccountUnderwriting,
  TEstimatedActivity
} from './types'
import {
  BUSINESS_DETAILS_LABELS,
  BUSINESS_PRESENCE_LABELS,
  BUSINESS_TYPES_LABELS,
  GEOGRAPHIC_REACH_LABELS,
  MONTHLY_VOLUME_RANGE_LABELS,
  PENDING_LITIGATION_LABELS
} from './constants'
import { formatBusinessAddress } from './utils'
import { FieldWithLabel } from 'components/FieldWithLabel/FieldWithLabel'
import { useCallback, useMemo } from 'react'
import { RowLayout } from 'features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import { Separator } from 'components/Separator/Separator'
import { LinkHighlighter } from 'components/LinkHighlighter/LinkHighlighter'
import { LABELS } from 'constants/txt'
import { formatPhoneNumber, safeCurrency } from 'helper/common'
import { TYPOGRAPHY_WEIGHT, Typography } from 'components/Typography'
import { ReactComponent as Attachment16 } from 'assets/svg/Attachment16.svg'

export const BusinessAccountDetails = ({
  businessAccountDetails,
  businessAccountUnderwriting,
  businessAccountDocuments
}: {
  businessAccountDetails: TBusinessAccountDetails
  businessAccountUnderwriting?: TBusinessAccountUnderwriting
  businessAccountDocuments: TBusinessAccountDocument[]
}) => {
  const addressLines = useMemo(() => {
    if (!businessAccountDetails?.address) return []
    return formatBusinessAddress(businessAccountDetails.address)
  }, [businessAccountDetails?.address])

  const undewritingCustomerType = useMemo(() => {
    if (
      !businessAccountUnderwriting ||
      !businessAccountUnderwriting.volume_share_by_customer_type
    ) {
      return '-'
    }
    const items: string[] = []

    const { business, consumer, p2p } =
      businessAccountUnderwriting.volume_share_by_customer_type

    if (business > 0) {
      items.push(`${business}% business`)
    }
    if (consumer > 0) {
      items.push(`${consumer}% consumer`)
    }
    if (p2p > 0) {
      items.push(`${p2p}% p2p`)
    }
    return items.length ? items.join(', ') : '-'
  }, [businessAccountUnderwriting])

  const getPaymentInfoSection = useCallback(
    (data?: TEstimatedActivity, title?: string) => (
      <>
        <Separator />
        {title && (
          <Typography.Body2 className="mt-24" weight={TYPOGRAPHY_WEIGHT.BOLD}>
            {title}
          </Typography.Body2>
        )}
        <div className="business-account-details__content-block__grid">
          <FieldWithLabel
            title={BUSINESS_DETAILS_LABELS.ESTIMATED_MONTHLY_VOLUME}
          >
            {data
              ? MONTHLY_VOLUME_RANGE_LABELS[data.monthly_volume_range]
              : '-'}
          </FieldWithLabel>
          <FieldWithLabel
            title={BUSINESS_DETAILS_LABELS.AVERAGE_TRANSACTION_AMOUNT}
          >
            {data ? safeCurrency(data.average_transaction_amount) : '-'}
          </FieldWithLabel>
          <FieldWithLabel
            title={BUSINESS_DETAILS_LABELS.MAXIMUM_TRANSACTION_AMOUNT}
          >
            {data ? safeCurrency(data.maximum_transaction_amount) : '-'}
          </FieldWithLabel>
        </div>
      </>
    ),
    []
  )

  const getIndustryFormattedLabel = useCallback(
    (industry?: string, industryCode?: string) => {
      if (!industry) return '-'
      if (!industryCode) return industry
      return `${industry} (${industryCode})`
    },
    []
  )

  return (
    <>
      <RowLayout title="Business information">
        <div className="business-account-details__content-block__grid">
          <FieldWithLabel title={BUSINESS_DETAILS_LABELS.LEGAL_BUSINESS_NAME}>
            {businessAccountDetails.legal_business_name ?? '-'}
          </FieldWithLabel>

          <FieldWithLabel title={BUSINESS_DETAILS_LABELS.DOING_BUSINESS_AS}>
            {businessAccountDetails.doing_business_as ?? '-'}
          </FieldWithLabel>

          <FieldWithLabel title={BUSINESS_DETAILS_LABELS.INDUSTRY}>
            {getIndustryFormattedLabel(
              businessAccountDetails.industry,
              businessAccountDetails.industry_code_mcc
            )}
          </FieldWithLabel>

          <FieldWithLabel title={BUSINESS_DETAILS_LABELS.BUSINESS_TYPE}>
            {businessAccountDetails.business_type
              ? BUSINESS_TYPES_LABELS[businessAccountDetails.business_type]
              : '-'}
          </FieldWithLabel>

          <FieldWithLabel title={BUSINESS_DETAILS_LABELS.EIN_OR_SSN}>
            {businessAccountDetails.is_tax_id_provided ? 'Provided' : '-'}
          </FieldWithLabel>

          <FieldWithLabel title={BUSINESS_DETAILS_LABELS.WEBSITE}>
            {businessAccountDetails.website ? (
              <LinkHighlighter text={businessAccountDetails.website} />
            ) : (
              '-'
            )}
          </FieldWithLabel>
        </div>

        <FieldWithLabel title={BUSINESS_DETAILS_LABELS.COMPANY_DESCRIPTION}>
          {businessAccountDetails.description ?? '-'}
        </FieldWithLabel>
      </RowLayout>
      <Separator />
      <RowLayout title="Business contact">
        <div className="business-account-details__content-block__grid">
          <FieldWithLabel title={LABELS.PHONE_NUMBER}>
            {businessAccountDetails.phone
              ? formatPhoneNumber(businessAccountDetails.phone)
              : '-'}
          </FieldWithLabel>

          <FieldWithLabel title={LABELS.EMAIL}>
            {businessAccountDetails.email ?? '-'}
          </FieldWithLabel>
        </div>

        <FieldWithLabel title={BUSINESS_DETAILS_LABELS.ADDRESS}>
          {businessAccountDetails.address
            ? addressLines.map((line) => <div key={line}>{line}</div>)
            : '-'}
        </FieldWithLabel>
      </RowLayout>
      <Separator />
      <RowLayout title="Business representatives">
        {!businessAccountDetails.representatives.length && '-'}
        {businessAccountDetails.representatives.map((representative, index) => (
          <RepresentativeItem key={index} representative={representative} />
        ))}
      </RowLayout>
      <Separator />
      <RowLayout title="Expected activity">
        <FieldWithLabel title={BUSINESS_DETAILS_LABELS.GEOGRAPHIC_REACH}>
          {businessAccountUnderwriting?.geographic_reach
            ? GEOGRAPHIC_REACH_LABELS[
                businessAccountUnderwriting.geographic_reach
              ]
            : '-'}
        </FieldWithLabel>
        {getPaymentInfoSection(
          businessAccountUnderwriting?.collect_funds.ach.estimated_activity,
          BUSINESS_DETAILS_LABELS.SENDING_PAYMENTS
        )}
        {getPaymentInfoSection(
          businessAccountUnderwriting?.send_funds.ach.estimated_activity,
          BUSINESS_DETAILS_LABELS.ACCEPTING_PAYMENTS
        )}
      </RowLayout>
      {businessAccountUnderwriting?.geographic_reach &&
        businessAccountUnderwriting?.business_presence && (
          <>
            <Separator />
            <RowLayout title="Underwriting">
              <div className="business-account-details__content-block__grid">
                <FieldWithLabel
                  title={BUSINESS_DETAILS_LABELS.BUSINESS_PRESENCE}
                >
                  {businessAccountUnderwriting?.business_presence
                    ? BUSINESS_PRESENCE_LABELS[
                        businessAccountUnderwriting.business_presence
                      ]
                    : '-'}
                </FieldWithLabel>

                <FieldWithLabel
                  title={BUSINESS_DETAILS_LABELS.PENDING_LITIGATION}
                >
                  {businessAccountUnderwriting?.pending_litigation
                    ? PENDING_LITIGATION_LABELS[
                        businessAccountUnderwriting.pending_litigation
                      ]
                    : '-'}
                </FieldWithLabel>

                <FieldWithLabel title={BUSINESS_DETAILS_LABELS.CUSTOMER_TYPE}>
                  {undewritingCustomerType}
                </FieldWithLabel>
              </div>
            </RowLayout>
          </>
        )}
      {!!businessAccountDocuments.length && (
        <>
          <Separator />
          <RowLayout title="Documents">
            <div className="column gap-16">
              {businessAccountDocuments.map((document) => (
                <div key={document.file_name} className="row gap-8">
                  <Attachment16 />
                  <Typography.Body2>{document.file_name}</Typography.Body2>
                </div>
              ))}
            </div>
          </RowLayout>
        </>
      )}
    </>
  )
}
